import {
  Component,
  OnInit,
  Input,
  ElementRef,
  ViewChild,
  EventEmitter,
  Output,
  ChangeDetectorRef
} from '@angular/core';
import {
  UntypedFormGroup,
  UntypedFormArray,
  UntypedFormBuilder,
  Validators,
  UntypedFormControl,
  ReactiveFormsModule
} from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ResolutionService } from '../../../../core/services/resolution.service';
import { trigger, transition, style, animate, group } from '@angular/animations';
import { BookingParametersInterface, ActioncodeInterface } from '../../../entities/bookingParameters';
import { FOLD_OUT_ANIMATION } from '../../../../core/animations/FOLD_OUT_ANIMATION';
import { ScrollService } from '../../../../core/services/scroll.service';
import { IconComponent, minus, plus, provideIcons, searchPeople, times } from '../../../icons';
import { AlpitranslatePipe } from '../../../../core/pipes/alpitranslate.pipe';

@Component({
  selector: 'app-person',
  templateUrl: './person.component.html',
  styleUrls: ['./person.component.scss'],
  animations: [
    FOLD_OUT_ANIMATION,
    trigger('delayedInfo', [
      transition(':enter', [
        group([
          style({ opacity: 0, height: 0, margin: 0, padding: 0, border: 0 }),
          animate('200ms 1500ms', style({ opacity: 1, height: '*', margin: '*', padding: '*', border: '*' }))
        ])
      ])
    ])
  ],
  providers: [provideIcons({ searchPeople, plus, minus, times })],
  imports: [IconComponent, AlpitranslatePipe, ReactiveFormsModule]
})
export class PersonComponent implements OnInit {
  @Input() subFormGroup: UntypedFormGroup;

  /**
   * boolean value, if this component is included on the start-page
   */
  @Input() isIncludedOnStart = false;

  /**
   * set the default selected amount of adults
   */
  @Input() inputAges: Array<number>;

  /**
   * set the maximum number of allowed persons for one booking (50 default)
   */
  @Input() maxAllowedPersons: number;

  /**
   * limit the maximum number of children that can be added in total (respects maxAllowedPersons)
   */
  @Input() maxAllowedChildren: number;

  /**
   * emit an number array with all ages of persons to book
   */
  @Output() changedPersons: EventEmitter<Array<number>> = new EventEmitter<Array<number>>();

  // used to debounce changes in personCount
  private personSubject$: Subject<number[]> = new Subject<number[]>();
  private fireEvents = true;
  private previousAdults = 0;

  personCount = 0;
  onPersonInfo: boolean; // used to shrink label

  @ViewChild('personCountInput', { static: false }) personCountInput: ElementRef;
  @ViewChild('adultInput', { static: true }) adultInput: ElementRef;

  constructor(
    private fb: UntypedFormBuilder,
    private resolutionService: ResolutionService,
    private cdr: ChangeDetectorRef,
    private scrollService: ScrollService
  ) {}

  get personCountHelper() {
    return this.subFormGroup.get('personCountHelper');
  }
  get adults() {
    return this.subFormGroup.get('adults');
  }
  get numberChildren() {
    return this.subFormGroup.get('numberChildren');
  }
  get children() {
    return this.subFormGroup.get('children') as UntypedFormArray;
  }

  @Input() bookingParameters: BookingParametersInterface;
  @Input() subFormGroupPromocode: UntypedFormControl;
  @Output() changedCouponCode: EventEmitter<ActioncodeInterface> = new EventEmitter<ActioncodeInterface>();

  // helper for inline service id
  @Input() subFormGroupServiceId: UntypedFormControl;
  @Output() changedServiceId: EventEmitter<string> = new EventEmitter<string>();

  @ViewChild('personForm', { static: true }) private personForm: ElementRef<HTMLElement>;

  toggleForm = false;

  ngOnInit() {
    this.maxAllowedChildren =
      this.maxAllowedChildren !== undefined ? this.maxAllowedChildren : this.maxAllowedPersons;
    setTimeout(() => {
      this.adults.setValue(0);
      this.children.clear();
      this.numberChildren.setValue(0);
      this.bookingParameters.ages
        .filter(value => value > 17)
        .forEach(() => {
          this.addAdult(true);
        });
      this.bookingParameters.ages
        .filter(value => value < 18)
        .forEach(age => {
          this.addChild(age, true);
        });
      this.updatePersonCount(true);
    });

    this.personSubject$
      .pipe(debounceTime(this.resolutionService.isMobileDevice() ? 0 : 400), distinctUntilChanged())
      .subscribe(newPersons => {
        if (this.fireEvents) {
          this.changedPersons.emit(newPersons);
        }
      });
  }

  /**
   * click to increase number of adults if the total number of persons is below 50
   */
  addAdult(dontMark?) {
    if (this.personCount < this.maxAllowedPersons && !this.adults.invalid) {
      this.adults.setValue(+this.adults.value + 1);
      this.updatePersonCount(dontMark);
    }
  }

  /**
   * click to decrease the number of adults if you have at least one adult left.
   */
  removeAdult() {
    if (+this.adults.value > 0 && !this.adults.invalid) {
      this.adults.setValue(+this.adults.value - 1);
      this.updatePersonCount();
    }
  }

  /**
   * user entered number of adults directy
   */
  changeAdult() {
    if (!this.adults.invalid) {
      if (+this.adults.value + +this.numberChildren.value > this.maxAllowedPersons) {
        this.adults.setValue(this.maxAllowedPersons - this.numberChildren.value);
      }
      if (this.previousAdults !== +this.adults.value) {
        this.previousAdults = +this.adults.value;
        this.updatePersonCount();
      }
    } else {
      this.adults.setValue(0);
    }
  }

  private addChildForm(age?: number) {
    this.children.push(this.fb.control(age ? age : '', Validators.required));
    this.children.markAsDirty();
  }

  /**
   * user clicked on "+". add a selectbox, update the person count
   */
  addChild(age?: number, dontMark?) {
    // current total persons is less than maxAllowedPersons and the input field is valid (only numbers)
    if (
      this.personCount < this.maxAllowedPersons &&
      this.children.length < this.maxAllowedChildren &&
      !this.numberChildren.invalid
    ) {
      this.addChildForm(age);
      this.numberChildren.setValue(+this.numberChildren.value + 1);
      this.updatePersonCount(dontMark);
    }
  }

  /**
   * user clicked on "-". remove the last selectbox, update the person count
   */
  removeChild() {
    // is the current numberChildren valid (just numbers) and is this number bigger than 0
    if (this.numberChildren.value > 0 && !this.numberChildren.invalid) {
      // remove the last selectbox
      this.children.removeAt(this.children.length - 1);
      // decrease the number in the input box
      this.numberChildren.setValue(+this.numberChildren.value - 1);
      // update the person count
      this.updatePersonCount();
    }
  }

  /**
   * the user entered directly into the numberChildren field.
   * update the number of child selectboxes, the personCount
   * @param input the current value of the input box
   */
  changeChild(input: string, dontMark = false) {
    if (!this.numberChildren.invalid) {
      if (+this.adults.value + +this.numberChildren.value > this.maxAllowedPersons) {
        this.numberChildren.setValue(this.maxAllowedPersons - this.adults.value);
      }

      // set number of child forms
      // too many children, remove
      if (this.children.length > this.numberChildren.value) {
        const removeNumber = this.children.length - this.numberChildren.value;
        for (let index = 0; index < removeNumber; index++) {
          this.children.removeAt(this.children.length - 1);
        }
      } else {
        // not enough children, add formfields
        const addNumber = this.numberChildren.value - this.children.length;
        for (let index = 0; index < addNumber; index++) {
          this.addChildForm();
        }
      }
      // update the person count
      this.updatePersonCount(dontMark);
    } else {
      // the input is invalid
      this.numberChildren.setValue(0);
      if (input === '') {
        // the user deleted all characters from the box.
      }
    }
  }

  /**
   * updates the total person count of adults and children.
   * If the form is valid, the changedPerson event will emit an array of all ages.
   */
  updatePersonCount(dontMark?) {
    // if (dontMark === undefined) {
    //   this.personCountHelper.markAsTouched();
    //   this.personCountHelper.markAsDirty();
    // }
    this.personCount = +this.adults.value + +this.numberChildren.value;

    // if the form is valid, update the search results
    const persons = new Array<number>();
    // add number of adults (age > 17)
    for (let index = 0; index < this.adults.value; index++) {
      persons.push(36);
    }
    // add children with age
    this.children.controls.forEach(control => {
      persons.push(+control.value);
    });
    if (!dontMark) {
      this.personSubject$.next(persons);
    }
    this.cdr.detectChanges();
  }

  public setFocus(behavior?: ScrollIntoViewOptions) {
    if (behavior) {
      this.scrollService.scrollIntoView(this.personCountInput.nativeElement);
      this.personCountHelper.markAsDirty();
      this.personCountHelper.markAsTouched();
    }
    this.open();
  }

  public focusAdultInput() {
    this.onPersonInfo = true;
    if (window.innerWidth > 992) {
      this.adultInput.nativeElement.focus();
    }
  }

  toggle() {
    this.toggleForm = !this.toggleForm;
    this.cdr.detectChanges();
  }

  open() {
    this.toggleForm = true;
    this.cdr.detectChanges();
  }
}
